import { create } from "zustand"
import { syncTabs } from "zustand-sync-tabs"
import type {
  TContact,
  TInteraction,
  TLastEvaluatedKey,
} from "@/schemas/contacts"
import { SortedMap } from "@/lib/SortedMap"

type FetchingState = "idle" | "fetching" | "first-fetching" | "error"

interface TDynamoStore {
  recentUserInteractions: SortedMap<TInteraction>
  _recentUserInteractionLastKey?: TLastEvaluatedKey
  isFetchingRecentUserInteractions: FetchingState
  recentAccountInteractions: SortedMap<TInteraction>
  _recentAccountInteractionLastKey?: TLastEvaluatedKey
  isFetchingRecentAccountInteractions: FetchingState
  recentUserContacts: SortedMap<TContact>
  _recentUserContactsLastKey?: TLastEvaluatedKey
  isFetchingRecentUserContacts: FetchingState
  recentAccountContacts: SortedMap<TContact>
  _recentAccountContactsLastKey?: TLastEvaluatedKey
  isFetchingRecentAccountContacts: FetchingState
  recentContactInteractions: Record<string, SortedMap<TInteraction>>
  _recentContactInteractionsLastKey: Record<string, TLastEvaluatedKey>
  isFetchingRecentContactInteractions: Record<string, FetchingState>
}

interface TDynamoStoreWithFunctions extends TDynamoStore {
  updateRecentUserInteractions: (
    interactions: Array<TInteraction>,
    lastKey?: TLastEvaluatedKey
  ) => void
  updateRecentAccountInteractions: (
    interactions: Array<TInteraction>,
    lastKey?: TLastEvaluatedKey
  ) => void
  updateRecentUserContacts: (
    contacts: Array<TContact>,
    lastKey?: TLastEvaluatedKey
  ) => void
  updateRecentAccountContacts: (
    contacts: Array<TContact>,
    lastKey?: TLastEvaluatedKey
  ) => void
  updateRecentContactInteractions: (
    contactId: string,
    interactions: Array<TInteraction>,
    lastKey?: TLastEvaluatedKey
  ) => void
  //
  setRecentUserInteractionLastKey: (lastKey: TLastEvaluatedKey) => void
  setRecentAccountInteractionLastKey: (lastKey: TLastEvaluatedKey) => void
  setRecentUserContactsLastKey: (lastKey: TLastEvaluatedKey) => void
  setRecentAccountContactsLastKey: (lastKey: TLastEvaluatedKey) => void
  setRecentContactInteractionsLastKey: (
    contactId: string,
    lastKey: TLastEvaluatedKey
  ) => void
  //
  clearRecentUserInteractions: () => void
  clearRecentAccountInteractions: () => void
  clearRecentUserContacts: () => void
  clearRecentAccountContacts: () => void
  clearRecentContactInteractions: (contactId: string) => void
  //
  setFetchingRecentUserInteractions: (state: FetchingState) => void
  setFetchingRecentAccountInteractions: (state: FetchingState) => void
  setFetchingRecentUserContacts: (state: FetchingState) => void
  setFetchingRecentAccountContacts: (state: FetchingState) => void
  setFetchingRecentContactInteractions: (
    contactId: string,
    state: FetchingState
  ) => void
}

const initialState: TDynamoStore = {
  recentUserInteractions: new SortedMap("RingId", "StartTimestamp"),
  recentAccountInteractions: new SortedMap("RingId", "StartTimestamp"),
  recentUserContacts: new SortedMap("ContactPersonId", "LastTimestamp"),
  recentAccountContacts: new SortedMap("ContactPersonId", "LastTimestamp"),
  recentContactInteractions: {},
  _recentUserInteractionLastKey: undefined,
  _recentAccountInteractionLastKey: undefined,
  _recentUserContactsLastKey: undefined,
  _recentAccountContactsLastKey: undefined,
  _recentContactInteractionsLastKey: {},
  isFetchingRecentUserInteractions: "idle",
  isFetchingRecentAccountInteractions: "idle",
  isFetchingRecentUserContacts: "idle",
  isFetchingRecentAccountContacts: "idle",
  isFetchingRecentContactInteractions: {},
}

export const useDynamoStore = create<TDynamoStoreWithFunctions>()((set) => ({
  ...initialState,
  /**
   * Add or update recent user interactions. Items that already exist will be updated, new items will be added.
   * @param interactions
   * @param lastKey
   */
  updateRecentUserInteractions: (interactions, lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      for (const interaction of interactions) {
        if (!interaction.RingId) continue

        clonedState.recentUserInteractions.set(interaction.RingId, interaction)
      }

      if (lastKey) clonedState._recentUserInteractionLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Add or update recent account interactions. Items that already exist will be updated, new items will be added.
   * @param interactions
   * @param lastKey
   */
  updateRecentAccountInteractions: (interactions, lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      for (const interaction of interactions) {
        if (!interaction.RingId) continue

        clonedState.recentAccountInteractions.set(
          interaction.RingId,
          interaction
        )
      }

      if (lastKey) clonedState._recentAccountInteractionLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Add or update recent user contacts. Items that already exist will be updated, new items will be added.
   * @param contacts
   * @param lastKey
   */
  updateRecentUserContacts: (contacts, lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      for (const contact of contacts) {
        if (!contact.ContactPersonId) continue

        clonedState.recentUserContacts.set(contact.ContactPersonId, contact)
      }

      if (lastKey) clonedState._recentUserContactsLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Add or update recent account contacts. Items that already exist will be updated, new items will be added.
   * @param contacts
   * @param lastKey
   */
  updateRecentAccountContacts: (contacts, lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      for (const contact of contacts) {
        if (!contact.ContactPersonId) continue

        clonedState.recentAccountContacts.set(contact.ContactPersonId, contact)
      }

      if (lastKey) clonedState._recentAccountContactsLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Add or update recent contact interactions. Items that already exist will be updated, new items will be added.
   * @param contactId
   * @param interactions
   * @param lastKey
   */
  updateRecentContactInteractions: (contactId, interactions, lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      if (!clonedState.recentContactInteractions[contactId]) {
        clonedState.recentContactInteractions[contactId] = new SortedMap(
          "RingId",
          "StartTimestamp"
        )
      }

      for (const interaction of interactions) {
        if (!interaction.RingId) continue

        clonedState.recentContactInteractions[contactId].set(
          interaction.RingId,
          interaction
        )
      }

      if (lastKey)
        clonedState._recentContactInteractionsLastKey[contactId] = lastKey

      return clonedState
    })
  },
  //
  /**
   * Set the last key for recent user interactions. This method is not recommended for direct use, it is better to
   * call the update method instead.
   * @param lastKey
   */
  setRecentUserInteractionLastKey: (lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState._recentUserInteractionLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Set the last key for recent account interactions. This method is not recommended for direct use, it is better to
   * call the update method instead.
   * @param lastKey
   */
  setRecentAccountInteractionLastKey: (lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState._recentAccountInteractionLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Set the last key for recent user contacts. This method is not recommended for direct use, it is better to
   * call the update method instead.
   * @param lastKey
   */
  setRecentUserContactsLastKey: (lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState._recentUserContactsLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Set the last key for recent account contacts. This method is not recommended for direct use, it is better to
   * call the update method instead.
   * @param lastKey
   */
  setRecentAccountContactsLastKey: (lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState._recentAccountContactsLastKey = lastKey

      return clonedState
    })
  },
  /**
   * Set the last key for recent contact interactions. This method is not recommended for direct use, it is better to
   * call the update method instead.
   * @param contactId
   * @param lastKey
   */
  setRecentContactInteractionsLastKey: (contactId, lastKey) => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState._recentContactInteractionsLastKey[contactId] = lastKey

      return clonedState
    })
  },
  //
  /**
   * Clear all recent user interactions.
   */
  clearRecentUserInteractions: () => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState.recentUserInteractions = new SortedMap(
        "RingId",
        "StartTimestamp"
      )
      clonedState._recentUserInteractionLastKey = undefined

      return clonedState
    })
  },
  /**
   * Clear all recent account interactions.
   */
  clearRecentAccountInteractions: () => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState.recentAccountInteractions = new SortedMap(
        "RingId",
        "StartTimestamp"
      )
      clonedState._recentAccountInteractionLastKey = undefined

      return clonedState
    })
  },
  /**
   * Clear all recent user contacts.
   */
  clearRecentUserContacts: () => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState.recentUserContacts = new SortedMap(
        "ContactPersonId",
        "LastTimestamp"
      )
      clonedState._recentUserContactsLastKey = undefined

      return clonedState
    })
  },
  /**
   * Clear all recent account contacts.
   */
  clearRecentAccountContacts: () => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState.recentAccountContacts = new SortedMap(
        "ContactPersonId",
        "LastTimestamp"
      )
      clonedState._recentAccountContactsLastKey = undefined

      return clonedState
    })
  },
  /**
   * Clear all recent contact interactions for a specific contact.
   * @param contactId
   */
  clearRecentContactInteractions: (contactId) => {
    return set((state) => {
      const clonedState = { ...state }
      clonedState.recentContactInteractions[contactId] = new SortedMap(
        "RingId",
        "StartTimestamp"
      )
      if (clonedState._recentContactInteractionsLastKey[contactId])
        delete clonedState._recentContactInteractionsLastKey[contactId]

      return clonedState
    })
  },
  //
  /**
   * Set the fetching state for recent user interactions.
   * @param fetchingState
   */
  setFetchingRecentUserInteractions: (fetchingState) => {
    return set((state) => {
      const clonedState = { ...state }

      let newFetchingState: FetchingState = fetchingState

      if (fetchingState === "fetching" || fetchingState === "first-fetching") {
        if (state._recentUserInteractionLastKey) {
          newFetchingState = "fetching"
        } else {
          newFetchingState = "first-fetching"
        }
      }

      clonedState.isFetchingRecentUserInteractions = newFetchingState

      return clonedState
    })
  },
  /**
   * Set the fetching state for recent account interactions.
   * @param fetchingState
   */
  setFetchingRecentAccountInteractions: (fetchingState) => {
    return set((state) => {
      const clonedState = { ...state }

      let newFetchingState: FetchingState = fetchingState

      if (fetchingState === "fetching" || fetchingState === "first-fetching") {
        if (state._recentAccountInteractionLastKey) {
          newFetchingState = "fetching"
        } else {
          newFetchingState = "first-fetching"
        }
      }

      clonedState.isFetchingRecentAccountInteractions = newFetchingState

      return clonedState
    })
  },
  /**
   * Set the fetching state for recent user contacts.
   * @param fetchingState
   */
  setFetchingRecentUserContacts: (fetchingState) => {
    return set((state) => {
      const clonedState = { ...state }

      let newFetchingState: FetchingState = fetchingState

      if (fetchingState === "fetching" || fetchingState === "first-fetching") {
        if (state._recentUserContactsLastKey) {
          newFetchingState = "fetching"
        } else {
          newFetchingState = "first-fetching"
        }
      }

      clonedState.isFetchingRecentUserContacts = newFetchingState

      return clonedState
    })
  },
  /**
   * Set the fetching state for recent account contacts.
   * @param fetchingState
   */
  setFetchingRecentAccountContacts: (fetchingState) => {
    return set((state) => {
      const clonedState = { ...state }

      let newFetchingState: FetchingState = fetchingState

      if (fetchingState === "fetching" || fetchingState === "first-fetching") {
        if (state._recentAccountContactsLastKey) {
          newFetchingState = "fetching"
        } else {
          newFetchingState = "first-fetching"
        }
      }

      clonedState.isFetchingRecentAccountContacts = newFetchingState

      return clonedState
    })
  },
  /**
   * Set the fetching state for recent contact interactions.
   * @param contactId
   * @param fetchingState
   */
  setFetchingRecentContactInteractions: (contactId, fetchingState) => {
    return set((state) => {
      const clonedState = { ...state }

      let newFetchingState: FetchingState = fetchingState

      if (fetchingState === "fetching" || fetchingState === "first-fetching") {
        if (state._recentContactInteractionsLastKey[contactId]) {
          newFetchingState = "fetching"
        } else {
          newFetchingState = "first-fetching"
        }
      }

      clonedState.isFetchingRecentContactInteractions[contactId] =
        newFetchingState

      return clonedState
    })
  },
}))
